/**
 * Copyright Highway9 Networks Inc.
 */

import React, { useState } from "react";
import { Box, Button, Chip, Divider, Popover, SxProps, Theme, Tooltip, Typography } from "@h9/ui-lib";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

export interface WarningChipItem {
  /** Unique key for the row. Falls back to name if omitted. */
  id?: string;
  /** Primary label — displayed as the item name. */
  name: string;
  /** Optional secondary label — displayed as a small chip (e.g. version, firmware). */
  secondary?: string;
}

export interface WarningChipProps {
  /** Items to display. Chip is hidden when empty. */
  items: WarningChipItem[];
  /**
   * Controls chip visibility. Typically driven by whether the
   * accompanying toast has been dismissed (isSuppressed from the callout hook).
   */
  visible: boolean;
  /** Text rendered inside the amber chip button (e.g. "2 mobile edges going out of support soon"). */
  label: string;
  /** Header shown in both the tooltip and the popover (e.g. "Mobile edge release going out of support soon. Please upgrade."). */
  header: string;
  /** Label for the footer action button. Defaults to "View all". */
  actionLabel?: string;
  /** Called when the footer "View all" button is clicked. */
  onViewAll: () => void;
  /** Maximum items shown in the popover before a "+N more" line. Defaults to 2. */
  maxItems?: number;
}

export function WarningChip({
  items,
  visible,
  label,
  header,
  actionLabel = "View all",
  onViewAll,
  maxItems = 2,
}: WarningChipProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (items.length === 0 || !visible) return null;

  const visibleItems = items.slice(0, maxItems);
  const hiddenCount = items.length - visibleItems.length;

  const handleChipClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);
  const handleViewAll = () => { handlePopoverClose(); onViewAll(); };

  const tooltipContent = (
    <Box>
      <Typography sx={styles.tooltipHeading}>{header}</Typography>
      {items.map((item) => (
        <Box key={item.id ?? item.name} sx={styles.tooltipRow}>
          <Typography component="span" sx={styles.tooltipItemName}>{item.name}</Typography>
          {item.secondary && (
            <Typography component="span" sx={styles.tooltipItemSecondary}>{item.secondary}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Tooltip
        title={tooltipContent}
        placement="bottom"
        slotProps={{
          tooltip: { sx: styles.tooltip },
          arrow: { sx: styles.tooltipArrow },
        }}
        arrow
      >
        <Box
          component="button"
          onClick={handleChipClick}
          sx={styles.chip}
          aria-label={label}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
        >
          <WarningAmberRounded sx={styles.chipIcon} />
          <Typography component="span" sx={styles.chipLabel}>{label}</Typography>
        </Box>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={styles.popover}
      >
        <Box sx={styles.popoverContent}>
          <Box sx={styles.popoverHeader}>
            <WarningAmberRounded sx={styles.popoverHeaderIcon} />
            <Typography sx={styles.popoverHeaderText}>{header}</Typography>
          </Box>
          <Divider />

          <Box sx={styles.itemList}>
            {visibleItems.map((item) => (
              <Box key={item.id ?? item.name} sx={styles.itemRow}>
                <Typography variant="body2" sx={styles.itemName}>{item.name}</Typography>
                {item.secondary && (
                  <Chip label={item.secondary} size="small" sx={styles.secondaryChip} />
                )}
              </Box>
            ))}
            {hiddenCount > 0 && (
              <Typography variant="caption" sx={styles.moreCount}>
                +{hiddenCount} more
              </Typography>
            )}
          </Box>

          <Divider />
          <Box sx={styles.popoverFooter}>
            <Button variant="text" size="small" onClick={handleViewAll} sx={styles.viewAllBtn}>
              {actionLabel}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

const styles = {
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    bgcolor: "#FAEEDA",
    color: "#854F0B",
    border: "none",
    borderRadius: "16px",
    px: "10px",
    py: "5px",
    cursor: "pointer",
    fontFamily: "inherit",
    flexShrink: 0,
    "&:hover": { bgcolor: "#f5e0c3" },
  },
  chipIcon: { fontSize: 16, color: "#854F0B", flexShrink: 0 },
  chipLabel: { fontSize: "13px", fontWeight: 600, color: "#854F0B", whiteSpace: "nowrap", lineHeight: 1 },
  tooltip: {
    bgcolor: "#fff",
    color: "text.primary",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    borderRadius: "8px",
    p: 1.25,
    maxWidth: 260,
  },
  tooltipArrow: { color: "#fff", "&::before": { border: "1px solid", borderColor: "divider" } },
  tooltipHeading: { fontSize: "12px", fontWeight: 600, color: "#854F0B", mb: 0.75, lineHeight: 1.4 },
  tooltipRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, py: 0.25 },
  tooltipItemName: { fontSize: "12px", color: "text.primary", fontWeight: 500 },
  tooltipItemSecondary: { fontSize: "11px", color: "text.secondary", whiteSpace: "nowrap" as const },
  popover: {
    "& .MuiPopover-paper": { width: 300, mt: 0.5, borderRadius: "8px", boxShadow: "0 4px 20px rgba(0,0,0,0.14)" },
  },
  popoverContent: { display: "flex", flexDirection: "column" },
  popoverHeader: { display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5 },
  popoverHeaderIcon: { fontSize: 18, color: "warning.main", flexShrink: 0 },
  popoverHeaderText: { fontWeight: 600, fontSize: "13px", color: "text.primary" },
  itemList: { px: 2, py: 1, display: "flex", flexDirection: "column", gap: 0.25 },
  itemRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, py: 0.75 },
  itemName: { color: "text.primary", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  moreCount: { color: "text.secondary", pt: 0.25 },
  secondaryChip: { bgcolor: "#F5F5F5", color: "text.secondary", fontSize: "11px", height: 20, flexShrink: 0, "& .MuiChip-label": { px: 1 } },
  popoverFooter: { px: 1, py: 0.5, display: "flex", justifyContent: "flex-end" },
  viewAllBtn: { fontSize: "13px", fontWeight: 600 },
} satisfies Record<string, SxProps<Theme>>;
